import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { EMPTY, forkJoin, merge, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { WINDOW, WindowWrapper } from '../../common/index';
import { Application, ApplicationGroupTile, ApplicationTile, ApplicationUrlType } from '../models/index';
import { MnApplicationsDataService } from '../services/applications.data.service';
import { UserSession } from './../../session/models/SessionUser';
import { GaSessionService } from './../../session/services/session.service';
import {
    applicationsAbortAction, applicationsCreateAction, applicationsCreateFailureAction,
    applicationsCreateSuccessAction, applicationsDeleteAction,
    applicationsDeleteFailureAction, applicationsDeleteSuccessAction, applicationsFailureAction, applicationsFindActiveAction,
    applicationsInitAction, applicationsSetActiveAction, applicationsSuccessAction,
    applicationsUpdateAction, applicationsUpdateFailureAction, applicationsUpdateSuccessAction
} from './applications.store';

@Injectable()
export class ApplicationsEffects {
    private getBaseRegex = new RegExp(/\/?(\w+)\/?.*/);

    constructor(
        private actions$: Actions,
        private applicationsDataService: MnApplicationsDataService,
        private route: ActivatedRoute,
        private sessionService: GaSessionService,
        @Inject(WINDOW) private window: WindowWrapper
    ) { }

    applications$ = createEffect(() => merge(...[
        this.actions$.pipe(
            ofType(
                applicationsInitAction,
                applicationsAbortAction
            ),
        ),
        this.sessionService.sessionUser$.pipe(filter(su => !!su))
    ]).pipe(
        switchMap((actionOrSession: Action | UserSession) => {
            if (actionOrSession.hasOwnProperty('type') && (actionOrSession as Action).type === applicationsAbortAction.type) {
                return EMPTY;
            }
            return forkJoin([
                this.applicationsDataService.readApplicationsGroups(),
                this.applicationsDataService.readApplications({ take: 500 })
            ]).pipe(
                map(([groups, apps]: [ApplicationGroupTile[], ApplicationTile[]]) => {
                    for (const app of apps) {
                        app.isHidden = app.params && app.params.hidden;
                    }
                    return applicationsSuccessAction({ applications: apps, groups });
                }),
                catchError(error => of(applicationsFailureAction({ error })))
            );
        })
    ));

    applicationsSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(applicationsSuccessAction),
        map(({ applications }) => {
            return applicationsFindActiveAction({ applications });
        })
    ));

    applicationsCreate$ = createEffect(() => this.actions$.pipe(
        ofType(applicationsCreateAction),
        switchMap(({ application }) => {
            return this.applicationsDataService.createApplication(application).pipe(
                map(app => applicationsCreateSuccessAction({ application: app })),
                catchError(error => of(applicationsCreateFailureAction({ error })))
            );
        })
    ));

    applicationsUpdate$ = createEffect(() => this.actions$.pipe(
        ofType(applicationsUpdateAction),
        switchMap(({ id, application }) => {
            return this.applicationsDataService.updateApplication(id, application).pipe(
                map(app => applicationsUpdateSuccessAction({ id: app.id, application: app })),
                catchError(error => of(applicationsUpdateFailureAction({ error })))
            );
        })
    ));

    applicationsDelete$ = createEffect(() => this.actions$.pipe(
        ofType(applicationsDeleteAction),
        switchMap(({ id }) => {
            return this.applicationsDataService.deleteApplication(id).pipe(
                map(() => applicationsDeleteSuccessAction({ id })),
                catchError(error => of(applicationsDeleteFailureAction({ error })))
            );
        })
    ));

    applicationsFindActive$ = createEffect(() => this.actions$.pipe(
        ofType(applicationsFindActiveAction),
        map(({ applications, force }) => {
            const base = this.getBase(this.window.location.pathname);
            const filteredApplications = applications.filter(app =>
                app.url && app.url.type === ApplicationUrlType.Inner && this.getBase(app.url.link) === base);
            return [filteredApplications, force, base];
        }),
        switchMap(([apps, force, base]: [ApplicationTile[], boolean, string]) => {
            const lsAppId = this.window.sessionStorage.getItem('activeApplicationId');
            if (lsAppId && !force) {
                const app = apps.find(a => a.id === lsAppId);
                if (app && this.isAppActive(app, base, 1)) {
                    return of(applicationsSetActiveAction({ id: app.id }));
                }
            }
            for (const app of apps) {
                if (this.isAppActive(app, base, apps.length)) {
                    return of(applicationsSetActiveAction({ id: app.id }));
                }
            }
            return of(applicationsSetActiveAction({ id: null }));
        })
    ));

    private getBase(url: string): string {
        const matches = url.match(this.getBaseRegex);
        return matches && matches.length > 1 ? matches[1] : '';
    }

    private isAppActive(app: Application | ApplicationTile, base: string, total?: number): boolean {
        if (!app.url || app.url.type !== ApplicationUrlType.Inner) { return false; }
        const appBase = this.getBase(app.url.link);
        if (appBase === base) {
            if (total === 1) { return true; }
            if (typeof app.url.state === 'undefined') { return false; }
            const stateParams = this.route.snapshot.queryParamMap;
            if (stateParams.keys.length > 0 && app.url.state_params) {
                for (const key of Object.keys(app.url.state_params)) {
                    if (app.url.state_params[key] !== stateParams.get(key)) { return false; }
                }
            }
            return true;
        }
        return false;
    }
}
